import { useState, useEffect } from 'react';

import { XMarkIcon, PlayIcon } from '@heroicons/react/24/outline';
import {
  LEARNING_COMPETENCY_TUTORIAL_DESKTOP_URL,
  LEARNING_COMPETENCY_TUTORIAL_MOBILE_URL,
  GENERIC_UNPACKING_TITLE,
} from 'apps/web/constants/genericUnpacking';
import {
  LP_TUTORIAL_DESKTOP_URL,
  LP_TUTORIAL_MOBILE_URL,
  LP_TITLE,
  LP_IPLAN_TUTORIAL_DESKTOP_URL,
  LP_IPLAN_TUTORIAL_MOBILE_URL,
  LP_TUTORIAL_VIDEO_URL,
} from 'apps/web/constants/lessonPlan';
import {
  OBJECTIVE_TITLE,
  OBJECTIVE_TUTORIAL_DESKTOP_URL,
  OBJECTIVE_TUTORIAL_MOBILE_URL,
  IP_OBJECTIVE_TITLE,
  IP_OBJECTIVE_TUTORIAL_DESKTOP_URL,
  IP_OBJECTIVE_TUTORIAL_MOBILE_URL,
} from 'apps/web/constants/objective';
import {
  TOPIC_EXPLORER_TUTORIAL_DESKTOP_URL,
  TOPIC_EXPLORER_TUTORIAL_MOBILE_URL,
  TOPIC_EXPLORER_BREAKDOWN_TUTORIAL_DESKTOP_URL,
  TOPIC_EXPLORER_BREAKDOWN_TUTORIAL_MOBILE_URL,
  TOPIC_EXPLORER_TITLE,
} from 'apps/web/constants/topicExplorer';
import clsx from 'clsx';
import { createPortal } from 'react-dom';

import { SkeletalRefLoaderMini } from './SkeletalRefLoader';

interface TutorialModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  variation?: string;
}

type TutorialType =
  | typeof LP_TITLE
  | typeof GENERIC_UNPACKING_TITLE
  | typeof TOPIC_EXPLORER_TITLE
  | typeof OBJECTIVE_TITLE
  | typeof IP_OBJECTIVE_TITLE;

interface TutorialData {
  desktop: string;
  mobile: string;
  alt: string;
  video?: string;
  variations?: Record<
    string,
    { desktop: string; mobile: string; alt: string; video?: string }
  >;
}

const TUTORIAL_MAPPING: Record<TutorialType, TutorialData> = {
  [LP_TITLE]: {
    desktop: LP_TUTORIAL_DESKTOP_URL,
    mobile: LP_TUTORIAL_MOBILE_URL,
    alt: 'Lesson Plan Tutorial',
    video: LP_TUTORIAL_VIDEO_URL,
    variations: {
      iplan: {
        desktop: LP_IPLAN_TUTORIAL_DESKTOP_URL,
        mobile: LP_IPLAN_TUTORIAL_MOBILE_URL,
        alt: 'Lesson Plan Tutorial',
        video: LP_TUTORIAL_VIDEO_URL,
      },
    },
  },
  [GENERIC_UNPACKING_TITLE]: {
    desktop: LEARNING_COMPETENCY_TUTORIAL_DESKTOP_URL,
    mobile: LEARNING_COMPETENCY_TUTORIAL_MOBILE_URL,
    alt: 'Learning Competency Tutorial',
  },
  [TOPIC_EXPLORER_TITLE]: {
    desktop: TOPIC_EXPLORER_TUTORIAL_DESKTOP_URL,
    mobile: TOPIC_EXPLORER_TUTORIAL_MOBILE_URL,
    alt: 'Topic Explorer Tutorial',
    variations: {
      explorer: {
        desktop: TOPIC_EXPLORER_TUTORIAL_DESKTOP_URL,
        mobile: TOPIC_EXPLORER_TUTORIAL_MOBILE_URL,
        alt: 'Topic Explorer Tutorial',
      },
      breakdown: {
        desktop: TOPIC_EXPLORER_BREAKDOWN_TUTORIAL_DESKTOP_URL,
        mobile: TOPIC_EXPLORER_BREAKDOWN_TUTORIAL_MOBILE_URL,
        alt: 'Topic Explorer Breakdown Tutorial',
      },
    },
  },
  [OBJECTIVE_TITLE]: {
    desktop: OBJECTIVE_TUTORIAL_DESKTOP_URL,
    mobile: OBJECTIVE_TUTORIAL_MOBILE_URL,
    alt: 'Objective Tutorial',
  },
  [IP_OBJECTIVE_TITLE]: {
    desktop: IP_OBJECTIVE_TUTORIAL_DESKTOP_URL,
    mobile: IP_OBJECTIVE_TUTORIAL_MOBILE_URL,
    alt: 'Iplan Objective Tutorial',
  },
};

const TutorialModal = ({
  title,
  isOpen,
  onClose,
  variation,
}: TutorialModalProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const baseTutorialData = TUTORIAL_MAPPING[title as TutorialType];
  const tutorialData =
    variation && baseTutorialData?.variations?.[variation]
      ? baseTutorialData.variations[variation]
      : baseTutorialData;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (isOpen && tutorialData) {
      setIsImageLoading(true);
      setImageError(false);
    }
  }, [isOpen, tutorialData, isMobile]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);

      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setIsImageLoading(false);
    setImageError(true);
  };

  const handleVideoTutorial = () => {
    if (tutorialData?.video) {
      window.open(tutorialData.video, '_blank', 'noopener noreferrer');
    }
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  if (!isOpen || !tutorialData) {
    return null;
  }

  const imageSrc = isMobile ? tutorialData.mobile : tutorialData.desktop;

  const modalContent = (
    <div className="fixed inset-0 z-50  flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative mx-4 max-h-[100vh] w-full max-w-7xl bg-white shadow-2xl">
        <div className="max-h-[calc(100vh-120px)] overflow-y-auto sm:p-6">
          <div className="space-y-4">
            {tutorialData?.video && (
              <button
                aria-label="Watch Video Tutorial"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-lg font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={handleVideoTutorial}
              >
                <PlayIcon className="h-6 w-6" />
                Watch Video Tutorial
              </button>
            )}
            <button
              className="absolute right-4 top-4 rounded-lg bg-gray-100 p-2 text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-600"
              onClick={onClose}
            >
              <XMarkIcon className="h-8 w-8 sm:h-12 sm:w-12" />
            </button>
            <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
              {isImageLoading && (
                <div className="h-[70vh] w-full p-12">
                  <SkeletalRefLoaderMini />
                </div>
              )}
              <img
                alt={tutorialData.alt}
                src={imageSrc}
                className={clsx(
                  'h-full w-full object-contain',
                  isImageLoading || imageError ? 'hidden' : 'block',
                )}
                onError={handleImageError}
                onLoad={handleImageLoad}
              />
            </div>
            <div className="text-center text-gray-600">
              <p className="px-2 pb-3">
                Follow the steps shown in the image above to get started with{' '}
                {title}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TutorialModal;
